/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import dayjs from 'dayjs';
import React from 'react';
import { TestIds } from '../../../testIds';
import { colors } from '@farmlands/mui-theme';
import { formatAgreementDates } from '../utils/formatAgreementDates';
import StatusChip from '../../../components/StatusChip';
import { StatusHeader } from '../../../components/StatusHeader';
import Typography from '../../../components/Typography';
import { Box, Stack, SxProps, Theme, styled } from '@mui/material';
import PercentageBar from './PercentageBar';
import { formatPrice } from '../../../util/helpers';

interface Props {
  agreementId: string;
  title: string;
  category?: string;
  status: string; //'active' | 'expired' | 'cancelled'
  startDate: Date;
  endDate: Date;
  children?: React.ReactNode;
  percentage?: number;
  totalProducts?: number;
  totalCost?: number;
  sxContainer?: SxProps<Theme>;
  drawdownButton?: React.ReactNode;
}

const statusMap = {
  effective: {
    display: 'Active',
    textColor: colors.shade0,
    bgColor: colors.secondary700,
  },
  closed: {
    display: 'Closed',
    textColor: colors.shade0,
    bgColor: colors.grey800,
  },
  'on hold': {
    display: 'On hold',
    textColor: colors.shade100,
    bgColor: colors.grey300,
  },
  upcoming: {
    display: 'Upcoming',
    textColor: colors.shade100,
    bgColor: colors.primary300,
  },
};

const Container = styled(Box)`
  position: relative;
  margin-bottom: 16px;
  &:before {
    content: '';
    position: absolute;
    top: -100px;
    left: -100px;
    right: -100px;
    bottom: -17px;
    background-color: ${colors.shade0};
    border-bottom: 1px solid ${colors.grey300};

    z-index: -1;
  }
`;
type Setting = (typeof statusMap)['effective'];

export const AgreementTitle = ({
  title,
  status,
  percentage,
  agreementId,
  drawdownButton,
  ...props
}: Props) => {
  const isFuture = dayjs(props.startDate).isAfter(Date.now());

  const setting: Setting = isFuture
    ? statusMap['upcoming']
    : (statusMap[status.toLowerCase() as keyof typeof statusMap] ?? {
        display: 'TBD',
        textColor: colors.shade0,
        bgColor: colors.secondary700,
      });

  return (
    <Container sx={props.sxContainer}>
      <StatusHeader
        statusChip={
          <StatusChip
            label={setting.display}
            variant='filled'
            bgColor={setting.bgColor}
            textColor={setting.textColor}
            data-testid={TestIds.components.statusHeader.statusBadge}
          />
        }
        title={title}
        drawdownButton={drawdownButton}
        text={
          <Stack width='100%'>
            <Stack gap={1}>
              <Typography variant='body2' gutterBottom>
                Agreement Number: {agreementId}
              </Typography>
              {<PercentageBar percentage={percentage ?? 0} gradient='primary' />}
            </Stack>
            <Stack mt={2} gap={2}>
              <Stack>
                <Typography variant='caption' color='grey800'>
                  Full agreement date:
                </Typography>
                <Typography variant='body1' color='primary700'>
                  {formatAgreementDates(props.startDate, props.endDate)}
                </Typography>
              </Stack>

              <Stack direction='row' justifyContent='space-between' maxWidth='300px'>
                <Stack>
                  <Typography variant='caption' color='grey800'>
                    Total products
                  </Typography>
                  <Typography variant='body1' color='primary700'>
                    {props.totalProducts}
                  </Typography>
                </Stack>

                <Stack>
                  <Typography variant='caption' color='grey800'>
                    Total cost
                  </Typography>
                  <Typography variant='body1' color='primary700'>
                    {formatPrice(props.totalCost ?? 0)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        }
      />
      <Box height={16} />
      {props.children}
    </Container>
  );
};
